import { useRouter } from 'next/router'
import { getClient } from '@lib/sanity'
import { loadTranslation } from '@lib/i18n'
import { useEffect, useState } from 'react'
import PostsIndex from 'components/PostsIndex'
import Loading from 'components/Loading'

const POSTS_PER_PAGE = 12

const SearchPage = () => {
  const router = useRouter()
  const [isReady, setIsReady] = useState(false)
  const [posts, setPosts] = useState()
  const [totalPosts, setTotalPosts] = useState(0)
  const [start, setStart] = useState(0)
  const [end, setEnd] = useState(0)

  useEffect(() => {
    function groqQuery(strings, ...keys) {
      return function (...values) {
        let dict = values[values.length - 1] || {}
        let result = [strings[0]]
        keys.forEach(function (key, i) {
          let value = Number.isInteger(key) ? values[key] : dict[key]
          result.push(value, strings[i + 1])
        })
        return result.join('')
      }
    }

    const makeQuery = groqQuery`{
      "results": *[ ${'filters'} ]${'order'} { ${'projections'} } [${'queryStart'}...${'queryEnd'}],
      "totalResults": count(*[ ${'filters'} ])
    }`

    if (router.isReady) {
      const selection = {
        _type: 'post',
        locale: router.locale,
      }
      const sorting = {
        sortKey: 'publishedAt',
        sortDirection: 'desc',
      }
      if (router.query.q) {
        selection.q = router.query.q
      }
      if (router.query.type) {
        if (Array.isArray(router.query.type)) {
          selection.type = [...router.query.type]
          if (router.query.type.length === 1) {
            sorting.sortKey = 'eventStart'
          }
        } else {
          selection.type = [router.query.type]
          sorting.sortKey = 'eventStart'
        }
      }
      if (router.query.org) {
        selection.org = Array.isArray(router.query.org)
          ? [...router.query.org]
          : [router.query.org]
      }
      if (router.query.region) {
        selection.regions = Array.isArray(router.query.region)
          ? [...router.query.region]
          : [router.query.region]
      }
      if (router.query.subject) {
        selection.subjects = Array.isArray(router.query.subject)
          ? [...router.query.subject]
          : [router.query.subject]
      }
      if (router.query.start) {
        selection.publishedAtStart = router.query.start
      }
      if (router.query.end) {
        selection.publishedAtEnd = router.query.end
      }
      if (router.query.edate) {
        selection.eventDate = router.query.edate
      }
      const queryStart =
        router.query.page === '1'
          ? 0
          : parseInt(router.query.page) * POSTS_PER_PAGE - POSTS_PER_PAGE
      const queryEnd = queryStart + POSTS_PER_PAGE

      setStart(queryStart)
      setEnd(queryEnd)

      const query = makeQuery({
        filters: Object.entries(selection)
          .reduce((result, entry) => {
            const [key, value] = entry
            if (key === 'q') {
              // TODO: also search body
              return [...result, `[title, body] match "${value}*"`]
            } else if (key === 'org') {
              return [...result, `references("${value.join('", "')}")`]
            } else if (key === 'publishedAtStart') {
              return [
                ...result,
                `dateTime(publishedAt) >= dateTime('${value}T00:00:00Z')`,
              ]
              return [...result]
            } else if (key === 'publishedAtEnd') {
              return [
                ...result,
                `dateTime(publishedAt) <= dateTime('${value}T23:59:59Z')`,
              ]
            } else if (key === 'eventDate') {
              return [
                ...result,
                `dateTime(eventStart) <= dateTime('${value}T00:00:00Z') && dateTime(eventEnd) >= dateTime('${value}T23:59:59Z')`,
              ]
            } else if (key === 'type') {
              return [...result, `${key} in ["${value.join('", "')}"]`]
            } else {
              if (Array.isArray(value)) {
                const qs = value.map((val) => `"${val}" in ${key}[]._ref`)
                return [...result, `(${qs.join(' || ')})`]
              } else if (typeof value === 'undefined') {
                return [...result]
              }
            }
            return [...result, `${key} == "${value}"`]
          }, [])
          .join(' && '),
        projections: [
          '_id',
          'title',
          '_type',
          'type',
          'mainImage',
          'excerpt',
          'body',
          'publishedAt',
          'eventStart',
          'eventEnd',
          'eventLocation',
          'slug',
        ].join(', '),
        order: (() => {
          if (!sorting) return ''
          const { sortKey, sortDirection } = sorting
          return ` | order(${sortKey} ${sortDirection})`
        })(),
        queryStart,
        queryEnd,
      })

      getClient(false)
        .fetch(query)
        .then((data) => {
          setPosts(data.results)
          setTotalPosts(data.totalResults)
          setIsReady(true)
        })
        .catch((err) => {
          console.error(err)
          setIsReady(true)
        })
    }
  }, [router])

  if (!isReady) {
    return <Loading />
  }

  return (
    <PostsIndex
      posts={posts}
      type="search"
      page={parseInt(router.query.page) || 1}
      totalPages={Math.ceil(totalPosts / POSTS_PER_PAGE)}
      totalPosts={totalPosts}
      start={start}
      end={end}
      showType={true}
    />
  )
}

export const getStaticProps = async ({ locale, preview = false }) => {
  const translation = await loadTranslation(
    locale,
    process.env.NODE_ENV === 'production'
  )

  return {
    props: {
      translation,
    },
  }
}

export const getStaticPaths = ({ locales }) => {
  const pagePaths = []
  locales.forEach((locale) => {
    pagePaths.push({
      params: {
        page: '1',
        locale,
      },
    })
  })
  return { paths: pagePaths, fallback: 'blocking' }
}

export default SearchPage
